import {
  CargoType,
  Odometer,
  DriverType,
  FuelType,
  TrailerType,
  Suspension,
  Ownership,
  RecordStatus,
  TenantRegistrationStatus,
  EldDeviceUsageType,
} from "./../data/service-proxies/api";
import { TranslationMessages } from "react-admin";
import englishMessages from "ra-language-english";

const customEnglishMessages: TranslationMessages = {
  ...englishMessages,
  menu: {
    dashboard: "Dashboard",
    hos: "Hours of Service",
    safety: "Safety",
    assets: "Assets",
    reports: "Reports",
    administration: "Administration",
    locations: "Locations",
  },
  languageNames: {
    en: "English",
    es: "Spanish",
    fr: "French",
    sr: "Serbian",
  },
  userMenu: {
    changeLanguage: "Change Language",
    switchTheme: {
      dark: "Switch to night mode",
      light: "Switch to day mode",
    },
  },
  timespan: {
    inPast: "%{time} ago",
    inFuture: "In %{time}",
  },
  arrayField: {
    countOthers: "and one other |||| and %{smart_count} others",
  },
  actions: {
    view: "View",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    delete: "Delete",
    confirmTitle: "Confirm this action",
    confirmContent: "Are you sure you want to perform this action?",
  },
  common: {
    errorLoadingData: "Error loading data",
    noData: "No data",
    mpg: "mpg",
    gal: "gal",
    truck: "Truck",
    driver: "Driver",
    average: "Average",
    max: "Max",
    min: "Min",
    speeding: "Speeding",
    accelerate: "Accelerate",
    idle: "Idle",
    worstPerformers: "Worst performers",
    rowsPerPage: "Rows per page",
    of: "of",
  },
  fuelEconomyReport: {
    fuelEconomyInMpg: "Fuel economy (MPG)",
    fuelConsumption: "Fuel consumption",
    morning: "Morning",
    noon: "Noon",
    afternoon: "Afternoon",
    night: "Night",
    speeding: "Speeding",
    normal: "Normal",
    slowPaced: "Slow paced",
    averageFuelConsumptionEfficiency: "Average fuel consumption efficiency",
    averageConsumption: "Average consumption",
    idleTimeConsumption: "Idle time consumption",
    speedingConsumption: "Speeding consumption",
    maxFuelConsumption: "Max fuel consumption",
    minFuelConsumption: "Min fuel consumption",
    accelerateConsumption: "Accelerate consumption",
    currentPeriod: "Current Period",
    previousPeriod: "Previous Period",
    fuelEconomyByAction: "Fuel economy by action",
    driversFuelConsumption: "Drivers fuel consumption",
    performance: "Performance",
    rank: "Rank",
    action: {
      idle: "Idle",
      speeding: "Speeding",
      acceleration: "Acceleration",
    },
  },
  idleReport: {
    idleOffDutyAverage: "Idle off duty average",
    idleOnDutyAverage: "Idle on duty average",
    idleModeFuelConsumption: "Idle mode fuel consumption",
    maxIdleTime: "Max idle time",
    minIdleTime: "Min idle time",
    averageIdleTime: "Average idle time",
    averageIdleTimeByTimeOfDay: "Avg. idle time",
    percentageFuelSpentIdle: "Percentage of total fuel spent during idle time",
    idleTime: "Idle time",
    idleTimeSpent: "Idle time spent (min)",
    idleConsumption: "Idle consumption (gal)",
    onDuty: "On duty",
    offDuty: "Off duty",
    totalIdle: "Total idle",
    maxIdle: "Max idle",
    minIdle: "Min idle",
    avgIdle: "Avg idle",
    avgFuelConsumption: "Avg. fuel consumpt.",
    totalFuelConsumption: "Total fuel consumption",
    driversTable: "Idle report by driver",
  },
  harshDrivingReport: {
    harshStop: "Harsh stop",
    harshBrake: "Harsh brake",
    harshBrakes: "Harsh brakes",
    harshStops: "Harsh stops",
    harshTurn: "Harsh turn",
    harshTurns: "Harsh turns",
    harshAcceleration: "Harsh acceleration",
    harshAccelerations: "Harsh accelerations",
    speeding: "Speeding",
    speedingOccurrences: "Speeding occurrences",
    speedingCount: "Speeding count",
    speedingAverage: "Speeding average",
    harshEvents: "Harsh events",
    harshEventsCountUnit: "harsh events",
    harshEventsActiveDriver: "Harsh Events Active Driver",
    harshDrivingTable: "Harsh driving by driver",
    activeDrivers: "Active drivers",
    drivers: "Drivers",
    alerts: "Alerts",
    bestPerformers: "Best performers",
    worstPerformers: "Worst performers",
    morning: "Morning",
    noon: "Noon",
    afternoon: "Afternoon",
    night: "Night",
  },
  messages: {
    reportGenerated: "Report generated successfully",
    reportFailed:
      "Report failed, please try again later. If the issue persists, please contact support",
    PasswordMismatch: "Incorrect password",
    NewPasswordMismatch: "Passwords entered do not match",
    authCallInitFirst:
      "Can't use the AuthManager without calling init() first.",
    apiCallInitFirst: "Can't use the ApiManager without calling init() first.",
    internalError:
      "An internal error has occurred, check the console for more information.",
    badRequest:
      "Server refused to validate request data, check the console for more information.",
    serverError:
      "A Server error has occurred, check the console for more information.",
    noResponse:
      "The request was made but no response was received. Ensure that you are connected to the Internet and the Server is running.",
    resetPasswordError:
      "Unable to reset your password at the moment. Please notify support.",
    resetPasswordSuccess: "A password reset email has been sent.",
    // ABP errors
    Event: {
      NoExistingEventForShipmentNumber:
        "Unable to change shipment number, no duty status change events found for requested day",
      NewDriverEventExists:
        "The specified driver already has a duty status change in the reassignment time frame",
      OriginalDriverFmcsaInspected:
        "Log of the source driver was inspected by FMCSA",
      TargetDriverFmcsaInspected:
        "Log of the target driver was inspected by FMCSA",
      FmcsaInspected:
        "One or more events is in the time frame which was inspected by FMCSA",
    },
    Identity: {
      DuplicateUserName: "The specified e-mail address is already in use.",
    },
    Company: {
      DuplicateCompanyName: "A company with the specified name already exists.",
      DuplicateDot: "A company with the specified DOT number already exists.",
      MultipleDutyCyclesPerCountry:
        "Only one duty cycle per country is allowed",
    },
    Driver: {
      DuplicateDriverId: "A driver with the specified ID already exists.",
      DuplicatePhoneNumber: "The phone number specified is already in use.",
      DuplicateLicence: "The license number specified is already in use.",
      NoActivityInSelectedTimeFrame:
        "No driver activity with the given vehicle in the selected time frame",
      Rollover: {
        BothDriversRequired:
          "Both drivers need to be selected for rollover to be performed",
        MustBeDifferentDrivers:
          "You have to select different drivers in order to perform a rollover",
      },
    },
    Vehicle: {
      InvalidVinFormat:
        "VIN must contain only numbers 0-9 and uppercase letters A-Z",
      InvalidVehicleId:
        "Vehicle ID must contain only numbers 0-9 and uppercase letters A-Z",
      DuplicateLicencePlateNumber:
        "There is already a vehicle with the specified license plate number.",
      DuplicateVehicleName: "The vehicle ID specified is already in use.",
    },
    EldDevice: {
      DuplicateSerialNumber:
        "There is already an ELD with the specified serial number",
      DuplicateSerialNumberOnHost:
        "The ELD with the specified serial number already exists, please contact your administrator to have it assigned to you.",
      ActionAllowedForTenantsOnly: "Action allowed only for tenants",
      HasOwnEldsDisabled:
        "You are not allowed to add your own ELD devices. Contact your administrator to enable this feature",
      AssignedToTenantVehicle:
        "Unable to perform this operation while the ELD device is assigned to a vehicle",
      NeedsCustomerAssignment:
        "You can't assign this device to a vehicle, please contact your administrator",
    },
    DvirForm: {
      DuplicateDvirFormName:
        "There is already a DVIR form with the specified name",
      DuplicateDvirFormAreaPointName:
        "There is already a DVIR form area point with the specified name",
      DuplicateDvirFormAreaName:
        "There is already a DVIR form area with the specified name",
    },
    Role: {
      AssignedToSomeone: "The role is assigned to at least one user",
      DefaultAlreadySet: "The default role already exists",
    },
    Volo: {
      Abp: {
        "Identity:DuplicateUserName":
          "The specified e-mail address is already in use.",
        "Identity:DuplicateRoleName":
          "A role with the specified name already exists",
      },
    },
    UserSynchronization: {
      WaitForAnotherOperationToComplete: {
        Delete:
          "This driver, or someone else on their behalf, is currently deactivating events in the given time frame. Please try again in a bit.",
        ReassignFrom:
          "This driver is currently having events in the given time frame reassigned from them to another driver. Please try again in a bit.",
        ReassignTo:
          "This driver is currently having events in the given time frame reassigned to them from another driver. Please try again in a bit.",
        ShiftEvents:
          "This driver is currently having an events manipulation operation (shift) in the given time frame. Please try again in a bit.",
        FixEvents:
          "This driver, or someone else on their behalf, is currently fixing events in the given time frame. Please try again in a bit.",
        Assign:
          "This driver is currently having previously unidentified events in the given time frame assigned to them. Please try again in a bit.",
        Batch:
          "This driver, or someone else on their behalf, is currently adding events in the given time frame. Please try again in a bit.",
        ResizeEvent:
          "This driver is currently having an events manipulation operation (resize) in the given time frame. Please try again in a bit.",
      },
    },
    ShiftEvents: {
      DifferentDriver:
        "The first and the last events don't belong to the same driver",
      EndEventBeforeStartEvent: "Start event must come before the end event",
      EventCollision:
        "Unable to shift events because there would be a collision with an existing event",
      LastEventInFuture:
        "Unable to shift events because one or more events would start in the future",
      SourceEventsInspectedByFmcsa: "Events were inspected by FMCSA",
      TargetTimeFrameInspectedByFmcsa:
        "There was a FMCSA inspection which covers the target time frame",
    },
    ResizeEvents: {
      CanOnlyResizeDutyStatusEvents: "Can only resize duty status events",
      NoLocationsForIntermediateEvents:
        "Unable to find a suitable location in this vehicle's location history to create one or more intermediate events",
      UnableToCreateIntermediateEvents:
        "Unable to create one or more intermediate events. Please contact support",
      ShrinkTooMuch: "Unable to shrink the duty status event that much",
      NextEventInFuture:
        "Unable to expand the duty status event because the next event would start in the future",
      NextEventCollision:
        "Unable to expand the duty status event because the next event would collide with another event",
    },
    MobileAppVersion: {
      DuplicateBuildNumber: "The specified mobile app version already exists",
    },
    VirtualTenants: {
      AlreadyCreated:
        "The requested customer already has a virtual customer created",
    },
    TenantRegistrations: {
      RegistrationRejected:
        "Deletion is only allowed for tenants with status 'Registration Rejected'",
      NoPermission: "You don't have permissions to use this action",
      InvalidStatus:
        "The specified status is not allowed for this tenant registration",
      InactiveTenant: "Inactive tenants can't be reassigned",
      RegistrationNotTiedCannotReassigned:
        "Registrations not tied to a created tenant can't be reassigned",
      NotCreatedTenant: "Tenants without tenant details can't be reassigned",
      CannotReassignedTo: "Inactive tenants can't be reassigned to",
      RegistrationNotTiedCannotReassignedTo:
        "Registrations not tied to a created tenant can't be reassigned to",
      NoMasterAccessReassign:
        "Tenants can't be reassigned to tenants without master access",
      DuplicateTenantName: "Tenant name already exists",
      DuplicateDomainName: "Domain name already exists",
      DuplicateAdminEmailAddress: "Email address already exists",
      InactiveTenantsCanNotAssignedTo: "Inactive tenants can't be assigned to",
      RegistrationNotTiedCannotAssignedTo:
        "Registrations not tied to a created tenant can't be assigned to",
      NoMasterAccessNewTenant:
        "Tenants can't be assigned to tenants without master access",
    },
    AssignEvent: {
      DrivingExists:
        "Unable to assign event to the driver because driving event already exists in this period.",
      NotAllowedYM: "Driver is not allowed to use yard move",
      NotAllowedPC: "Driver is not allowed to use personal conveyance",
      OverLappingEvent:
        "Unable to assign event to the driver because there is an event that start from same time.",
    },
  },
  permissions: {
    fieldAccessLevel: {
      Full: "Full access",
      ReadOnly: "Read-only access",
      Hidden: "No access (field hidden)",
    },
    AbpIdentity: "Identity",
    Roles: "Roles",
    FeatureManagement: "Features",
    AbpTenantManagement: "Customer management",
    Administration: "Administration",
    Hos: "Hours of Service",
    Locations: "Locations",
    Assets: "Assets",
    Reports: "Reports",
    Users: "Users",
    Tenants: "Tenants",
    Drivers: "Drivers",
    FleetManagers: "Fleet managers",
    MasterFleetManagers: "Master users",
    Companies: "Companies",
    Vehicles: "Vehicles",
    Trailers: "Trailers",
    Groups: "Groups",
    NotificationSubscriptions: "Notification Subscriptions",
    Elds: "ELDs",
    MasterElds: "ELDs (master)",
    Logs: "Logs",
    DemoAccounts: "Demo accounts",
    VirtualTenants: "Virtual tenants",
    Violations: "Violations",
    UnidentifiedEvents: "Unidentified events",
    EldDisconnections: "ELD disconnections",
    EldMalfunctions: "ELD malfunctions",
    FmcsaDataTransfer: "FMCSA data transfer",
    Current: "Current",
    History: "History",
    DvirHistory: "DVIR history",
    Maintenance: "Maintenance",
    Diagnostics: "Diagnostics",
    General: "General",
    DvirForms: "DVIR forms",
    TenantRegistrations: "Customer registrations",
    TrailerTrackingProviders: "Trailer GPS providers",
    ApiClients: "API clients",
    FlowApiClients: "Flow API clients",
    Safety: "Safety",
    Alerts: "Alerts",
    MobileAppVersions: "Mobile app versions",
    FuelProviders: "Fuel providers",
    ClientPermissions: "Client",
    VehicleTrackingProviders: "Vehicle Tracking providers",

    // Permission names
    Access: "Permission to access",
    View: "Permission to view",
    Create: "Permission to create",
    Update: "Permission to update",
    Delete: "Permission to delete",
    ManagePermissions: "Permission to edit permissions",
    Ifta: "Permission to access IFTA report",
    VehicleMileage: "Permission to access vehicle mileage report",
    VehicleDistance: "Permission to access vehicle distance report",
    VehicleInactivity: "Permission to access vehicle inactivity report",
    LocationsHistory: "Permission to access vehicle locations history report",
    EldEvents: "Permission to access ELD events report",
    TripReportDetail: "Permission to access trip report detail",
    TripDetail: "Permission to access trip detail",
    MasterTripReportDetail: "Permission to access master trip report detail",
    MasterTripDetail: "Permission to access master trip detail",
    SafetyReport: "Permission to access safety report",
    Acknowledge: "Permission to acknowledge",
    ManageDefects: "Permission to manage DVIR defects",
    ManageShareLinks: "Permission to manage share links",
    LiveTracking: "Permission to access live tracking",
    Download: "Permission to download",
    Reassign: "Permission to reassign",
    AddException: "Permission to manage HOS exceptions",
    AddDocuments: "Permission to manage violation documents",
    Assign: "Permission to assign",
    UserLookup: "Permission to lookup users",
    ManageHostFeatures: "Permission to edit application features",
    ManageFeatures: "Permission to edit tenant enabled features",
    ManageConnectionStrings: "Permission to edit tenant connection strings",
    Manage: "Permission to manage",
    Rollover: "Permission to perform rollover",
    MarkUnofficial: "Permission to mark unofficial",
    SendCommands: "Permission to send commands",
    Resize: "Permission to resize",
    Certify: "Permission to certify",
    Deactivate: "Permission to deactivate",
    ViewEldDeviceUsage: "Permission to view ELD device usage",
    AccessAnyTenant: "Tenant",
    // Field permissions
    fieldPermissions: {
      MasterEldDevice: {
        tabName: "ELD devices",
        MasterEldDeviceDto: "Updating an ELD device",
        fields: {
          AssignedToTenantId: "Assigned to tenant",
          DebuggingEnabled: "Debugging enabled",
          DataPlanEnabled: "Data plan enabled",
          Note: "Note",
        },
      },
      EldDevice: {
        tabName: "ELD devices",
        CreateEldDeviceDto: "Creating an ELD device",
        EldDeviceListDto: "Viewing a list of ELD devices",
        UpdateEldDeviceDto: "Updating an ELD device",
        fields: {
          SerialNumber: "S/N",
          Vendor: "Vendor",
          VehicleName: "Assigned to vehicle",
          DeviceType: "Device type",
        },
      },
      DvirForm: {
        tabName: "DVIR forms",
        UpdateDvirFormDto: "Updating a DVIR form",
        fields: {
          Name: "Name",
          IsDefault: "Is default",
          Areas: "Areas",
        },
      },
      Group: {
        tabName: "Groups",
        CreateUpdateGroupDto: "Creating or editing a group",
        GroupListDto: "Viewing a list of groups",
        fields: {
          Name: "Name",
          CompanyId: "Company",
          DriverIds: "Drivers",
          VehicleIds: "Vehicles",
          CreatorUserDisplayName: "Creator user",
          DriverNames: "Drivers",
          VehicleNames: "Vehicles",
        },
      },
      FleetManager: {
        tabName: "Fleet managers",
        CreateFleetManagerDto: "Creating a fleet manager",
        FleetManagerListDto: "Viewing a list of fleet managers",
        UpdateFleetManagerDto: "Updating a fleet manager",
        fields: {
          CompanyIds: "Company",
          Companies: "Companies",
          CanEditLogs: "Can edit logs",
          CanResolveDefects: "Can resolve defects",
          GroupIds: "Groups",
          Email: "E-mail",
          Groups: "Groups",
          PhoneNumber: "Phone number",
          Role: "Role",
          Name: "First name",
          Surname: "Last name",
          RoleName: "Role",
        },
      },
      MasterFleetManager: {
        tabName: "Master users",
        UpdateMasterFleetManagerDto: "Updating a master user",
        fields: {
          Name: "First name",
          Surname: "Last name",
          PhoneNumber: "Phone number",
          RoleName: "Role",
        },
      },
      Driver: {
        tabName: "Drivers",
        CreateDriverDto: "Creating a driver",
        UpdateDriverDto: "Updating a driver",
        DriverListDto: "Viewing a list of drivers",
        fields: {
          RoleName: "Role",
          AllowYardMove: "Allow Yard Move",
          AllowPersonalConveyance: "Allow Personal Conveyance",
          AllowManualDriveTime: "Allow manual drive time",
          HideUnidentifiedEvents: "Hide unidentified events",
          BrakeException30Minutes: "30 minute break exception",
          CycleReset24Hours: "24 hour cycle reset",
          Documents: "Documents",
          DrivingCycles: "Driving cycles",
          DocumentNames: "Documents",
          Email: "E-mail",
          Groups: "Groups",
          PersonalAllowed: "Personal Conveyance allowed",
          PhoneNumber: "Phone number",
          YardMoveAllowed: "Yard Move allowed",
          Name: "First name",
          Surname: "Last name",
          DriverId: "Driver ID",
          DateOfBirth: "Date of birth",
          DriverType: "Driver type",
          LicenceCountryId: "License country",
          LicenceStateId: "License state",
          LicenceNumber: "License number",
          ExemptFromEld: "Exempt from ELD",
          CargoType: "Cargo type",
          Address: "Address",
          CompanyId: "Company",
          CycleRules: "Cycle rules",
          AllowLogEditing: "Allow log editing",
        },
      },
      Vehicle: {
        tabName: "Vehicles",
        VehicleDto: "Viewing a vehicle",
        CreateVehicleDto: "Creating a vehicle",
        UpdateVehicleDto: "Editing a vehicle",
        VehicleListDto: "Viewing a list of vehicles",
        fields: {
          Name: "Vehicle ID",
          Vin: "VIN",
          Year: "Year",
          Make: "Make",
          Model: "Model",
          Color: "Color",
          FuelType: "Fuel type",
          CountryId: "License country",
          Country: "License country",
          StateId: "License state",
          State: "License state",
          LicensePlate: "License plate",
          Company: "Company",
          CompanyId: "Company",
          Odometer: "Odometer",
          CompanyOwned: "Company owned",
          EldDeviceId: "ELD S/N",
          EldSerialNumber: "ELD S/N",
          Documents: "Documents",
          DocumentNames: "Document names",
          Groups: "Groups",
          HasSleeperBerth: "Has sleeper berth",
        },
      },
      Company: {
        tabName: "Companies",
        CompanyDto: "Viewing a company",
        CreateCompanyDto: "Creating a company",
        UpdateCompanyDto: "Editing a company",
        CompanyListDto: "Viewing companies list",
        fields: {
          Name: "Name",
          Street: "Street",
          City: "City",
          PhoneNumber: "Phone number",
          Email: "E-mail address",
          CountryId: "Country",
          Country: "Country",
          StateId: "State",
          State: "State",
          PostalCode: "Postal/ZIP code",
          Dot: "DOT #",
          HomeTerminalTimeZone: "Home terminal time zone",
          CargoType: "Default cargo type",
          ThirtyMinuteBreakException: "Default 30-minute break exception",
          TwentyFourHrCycleReset: "Default 24-hour cycle reset",
          DvirWarnings: "DVIR warnings",
          Odometer: "Default odometer type",
          FleetOverSpeedingThreshold: "Fleet over speeding threshold",
          DutyCycles: "Default duty cycles",
          CycleRules: "Default cycle rules",
          Documents: "Documents",
        },
      },
      Trailer: {
        tabName: "Trailers",
        TrailerDto: "Viewing a trailer",
        CreateTrailerDto: "Creating a trailer",
        UpdateTrailerDto: "Editing a trailer",
        TrailerListDto: "Viewing trailers list",
        fields: {
          Name: "Trailer ID",
          TrailerType: "Trailer type",
          Year: "Year",
          Make: "Make",
          Vin: "VIN",
          LicensePlate: "License plate",
          Ownership: "Ownership",
          Suspension: "Suspension type",
          CompanyId: "Company",
          Company: "Company",
          GpsSerial: "GPS S/N",
          Documents: "Documents",
          DocumentNames: "Document names",
        },
      },
      NotificationSubscription: {
        tabName: "Notification subscriptions",
        NotificationSubscriptionListDto:
          "Viewing notification subscriptions list",
        NotificationSubscriptionDto:
          "Creating or editing a subscription notification",
        fields: {
          EmailRecipients: "E-mail recipients",
          SmsRecipients: "SMS recipients",
          SendWindowStartTime: "Don't send before",
          SendWindowEndTime: "Don't send after",
          EventPreferences: "Events",
          SelectedDrivers: "Drivers",
          SelectedVehicles: "Vehicles",
          IsAllDriversSelected: "Can select all drivers",
          IsAllVehiclesSelected: "Can select all vehicles",
        },
      },
    },
  },
  resources: {
    sensorAttributes: {
      ECU_AMBIENT_TEMP: "Ambient temperature",
      ECU_COOLANT_TEMP: "Engine coolant temperature",
      ECU_ENGINE_HOURS: "Total engine working hours",
      ECU_FUELLVL: "Fuel level",
      ECU_IDLE_FUEL_USED: "Total fuel used idling",
      ECU_IDLE_HOURS: "Total idling hours (ECU)",
      ECU_MPG: "Total average MPG",
      ECU_ODOMETER_MILES: "Odometer (ECU)",
      ECU_OIL_TEMP: "Engine oil temperature",
      ECU_PTO_HOURS: "Total PTO hours",
      ECU_RPM: "Current RPM",
      ECU_SPEED_MILES: "Current speed (ECU)",
      ECU_THROTTLE: "Current throttle position",
      ECU_TOTAL_FUEL_USED: "Total fuel used",
      ECU_TRIP_FUEL_USED: "Trip fuel used",
      ECU_VIN: "VIN",
      ECU_VOLTAGE: "Battery voltage (ECU)",
      EXT_VOLTAGE: "Battery voltage",
      HEADING_DEGREES: "Vehicle movement heading",
      IGNITION: "Ignition state",
      IGNITION_ON_DUR: "Total time ignition was on",
      J1939_COOLANT_LEVEL: "Engine coolant level",
      J1939_DIESEL_EXHAUST_FLUID_LEVEL: "Diesel exhaust fluid level",
      J1939_DIESEL_EXHAUST_FLUID_TEMPERATURE:
        "Diesel exhaust fluid temperature",
      J1939_DPF_SOOT_LOAD: "DPF soot load",
      J1939_DPF_SOOT_LOAD_REGENERATION_THRESHOLD:
        "DPF soot load regen threshold",
      J1939_DPF_TIME_SINCE_LAST_ACTIVE_REGENERATION:
        "Time since last DPF regen",
      J1939_MAX_VEHICLE_SPEED_LIMIT: "Max vehicle speed limit",
      J1939_TRANSMISSION_OIL_LEVEL: "Tramission oil level",
      J1939_TRANSMISSION_OIL_TEMPERATURE: "Transmission oil temperature",
      J1939_ENGINE_OIL_LEVEL: "Engine oil level",
      J1939_SEATBELT_SWITCH: "Seatbelt switch",
      J1939_TRANSMISSION_CURRENT_GEAR: "Current transmission gear",
      NUM_SATELLITES: "Number of GPS satellites used",
      OBD_INSTANT_MPG: "Instant MPG",
      OBD_TRIP_MPG: "Trip MPG",
      ODOMETER_MILES: "Odometer (ELD)",
      SPEED_MPH: "Current speed (ELD)",
      TOTAL_IDLE_DUR: "Total idling duration (ELD)",
      TRUE_ODOMETER_MILES: "True odometer",
    },
    common: {
      labels: {
        licenceIssuingCountry: "License issuing country",
        licenceIssuingState: "License issuing state",
        drivingCountry: "Driving country",
        cycleRule: "Cycle rule",
        documents: "Documents",
        backToList: "Back to list",
        documentName: "Document name",
        customDocumentName: "Custom document name",
        expirationDate: "Expiration date",
        fileName: "File name",
        downloadLink: "Download link",
        download: "Download",
        phone: "Phone number",
        email: "E-mail",
        country: "Country",
        state: "State",
        groups: "Groups",
        search: "Search",
        active: "Active",
        delete_title: "Delete %{name}",
        mustBeValidPhoneNumber: "Must be a valid phone number",
        mustBeNumericOnly: "Can only contain numbers",
        allAssets: "All assets",
        all: "All",
        selectTenant: "Select tenant",
        notSet: "Not set",
        futureTimeNotAllowed: "The time in the future is not allowed",
        valueNotInAllowedRange: "Must be from %{from} to %{to}",
        invalidForm: "The data entered in the form is not valid",
        dateFrom: "Date from",
        dateTo: "Date to",
      },
    },
    "master-eld-usage-report-archive": {
      name: "ELD usage reports",
      forcedCaseName: "ELD usage reports",
    },
    "safety-alerts": {
      name: "Safety alerts",
    },
    "safety-dashboard": {
      name: "Dashboard",
      labels: {
        fullName: "Full name",
        dateOfBirth: "Date of birth",
        email: "E-mail",
        address: "Address",
        phoneNumber: "Phone number",
        mobileAppType: "Mobile app type",
        mobileAppVersion: "Mobile app version",
        vehicle: "Vehicle",
      },
    },
    drivers: {
      name: "Drivers",
      labels: {
        filterByDriverStatus: "Filter by driver status",
        filterByDutyStatus: "Filter by duty status",
        driverId: "ID",
        driver: "Driver",
        vehicle: "Vehicle",
        trailer: "Trailer",
        dutyStatus: "Duty status",
        untilBreak: "Until break",
        toDrive: "To drive",
        shiftTimeLeft: "Shift time left",
        cycleTimeLeft: "Cycle time left",
        lastPosition: "Last position",
        eldConnection: "ELD connection",
        violations: "Violations",
        openCurrentLocationView: "Open the current location view",
        openCurrentDriverLog: "Open the current driver log",
        driverDidntHaveViolations: "The driver didn't have violations",
        driverHadViolations: "The driver had violations",
        none: "None",
        unknown: "Unknown",
        lastActivity: "Last activity",
      },
    },
    "hos-logs": {
      name: "Logs",
      labels: {
        dateFrom: "Display activity from",
        dateTo: "Display activity to",
        fullName: "Driver name",
        hasViolations: "Violations",
        lastSync: "Last activity",
        homeTerminalTimeZone: "Home terminal time zone",
      },
    },
    "hos-driver-logs": {
      name: "Driver logs",
      labels: {
        date: "Date",
        distanceInMiles: "Distance (mi)",
        minutesWorked: "Time on duty",
        hasInspectionReport: "Has inspection report",
        foundDefects: "Defects found",
        signed: "Log certified",
        violations: "Violations",
        noViolations: "No violations",
      },
    },
    "hos-driver-log-view": {
      name: "Driver log view",
      labels: {
        driver: "Driver",
        cycleRule: "Cycle rule",
        hoursWorked: "Hours worked",
        violations: "Violations",
        noViolations: "No violations",
        coDrivers: "Co-drivers",
        logForm: "Log form",
        vehicle: "Vehicle(s)",
        trailers: "Trailer(s)",
        distance: "Distance",
        company: "Company",
        mainOffice: "Main office",
        homeTerminal: "Home terminal",
        shippingDocs: "Shipping docs",
        notes: "Notes",
        noCoDrivers: "No co-drivers",
        driverSignature: "Driver signature",
        notCertified: "The log is not certified.",
        inspectionReports: "Inspection reports",
        dateTime: "Date & time",
        defects: "Defects",
        noDefects: "No defects found",
        signed: "Signed",
        odometer: "Odometer",
        basic: "Basic",
        detailed: "Detailed",
        status: "Status",
        start: "Start",
        duration: "Duration",
        location: "Location",
        engineHours: "Engine Hours",
        origin: "Origin",
        reassignEvents: "Reassign events",
        confirmDeleteTitle: "Delete event",
        confirmDeleteMessage:
          "Are you sure you want to delete this event? This action can't be undone.",
        confirmBulkDeleteTitle: "Delete events",
        confirmBulkDeleteMessage:
          "Are you sure you want to delete these events? This action can't be undone.",
        confirmDeleteExceptionTitle: "Remove exception",
        confirmDeleteExceptionMessage:
          "Are you sure you want to remove this exception?",
        startEvent: "Start event",
        endEvent: "End event",
        changeShippingDocsSuccess: "Shipping docs set successfully",
        changeShippingDocsFailed: "Unable to set the shipping docs",
        addRemark: "Add remark",
        addStatus: "Add status",
        editEvents: "Edit events",
        country: "Country",
        state: "State",
        exception: "Exception",
        exceptions: "Exceptions",
        addException: "Add exception",
        setDrivingNoteFailed: "Unable to set the note",
        setDrivingNoteSuccess: "Note set successfully",
      },
      logSources: {
        AutomaticallyRecordedByEld: "Auto",
        EditedOrEnteredByTheDriver: "Manual",
        EditRequestedByAnAuthenticatedUserOtherThanTheDriver: "Fleet manager",
        AssumedFromUnidentifiedDriverProfile: "Auto-assumed",
      },
      messages: {
        reassignSuccess: "Events reassigned successfully",
        rolloverSuccess: "Rollover completed successfully",
        shiftSuccess: "Events shifted successfully",
        resizeSuccess: "Event resized successfully",
        changeEventsTrailersSuccess: "Trailer(s) updated succesfully",
        changeEventsShippingDocsSuccess: "Shipping docs updated successfully",
        changeEventsCoDriverSuccess: "Co-driver updated succesfully",
        changeEventsVehicleSuccess: "Vehicle updated successfully",
      },
    },
    "hos-events-master": {
      notifications: {
        editSuccess: "Status event edited successfully",
        createSuccess: "Status event created successfully",
      },
    },
    "hos-violations": {
      name: "Violations",
      labels: {
        fullName: "Driver",
        noViolations: "No violations",
        violations: "Committed violations",
        dateFrom: "Display violations from",
        dateTo: "Display violations to",
        violationsCount: "Violations count",
      },
    },
    "hos-drivers": {
      name: "Driver activities",
    },
    "hos-driver-violations": {
      name: "Driver violations",
      labels: {
        filterByViolation: "Filter by violation",
        time: "Date & time",
        type: "Violation type",
        viewDocuments: "View documents",
      },
    },
    "hos-driver-violation-documents": {
      name: "Documents",
      labels: {
        date: "Document date",
        documentName: "Document name",
        fileName: "Download link",
        note: "Note",
        updatedBy: "Updated by",
      },
    },
    "hos-unidentified-driving": {
      name: "Unidentified events",
      labels: {
        vehicleName: "Vehicle ID",
        time: "Unidentified event date & time",
        accumulatedDistance: "Accumulated distance",
        lastLocation: "Last location",
        dateFrom: "From date",
        dateTo: "To date",
        driver: "Driver",
        assign: "Assign...",
        startTime: "Start time",
        duration: "Duration",
        startLocation: "Start location",
        endLocation: "End location",
        distance: "Distance",
      },
    },
    "hos-eld-disconnections": {
      name: "ELD disconnections",
      labels: {
        dateFrom: "From date",
        dateTo: "To date",
        vehicleName: "Vehicle ID",
        lastDisconnectedEventTime: "Last status change",
        isConnectedNow: "Current ELD connection status",
        driverFullName: "Driver",
        currentVehicleLocation: "Current location",
        lastDisconnectedDurationMinutes: "Status duration",
      },
    },
    "hos-disconnected-drivings": {
      name: "Disconnected Driving",
      empty:
        "No possible disconnected-driving warnings were found for this period.",
      labels: {
        vehicleName: "Vehicle",
        driver: "Driver",
        notAttributed: "No driver attribution available",
        dateFrom: "From date",
        dateTo: "To date",
        startTime: "Start",
        endTime: "End",
        duration: "Duration",
        ongoing: "Ongoing",
      },
      validation: {
        invalidRange: "To date must be on or after from date.",
        maximumRange: "Select no more than 31 inclusive days.",
      },
      states: {
        loading: "Loading disconnected-driving evidence…",
        error: "Disconnected-driving evidence could not be loaded.",
        retry: "Try again",
      },
    },
    "hos-eld-malfunctions": {
      name: "ELD Malfunctions",
      labels: {
        dateFrom: "Malfunctions from",
        dateTo: "Malfunctions to",
        vehicleName: "Vehicle ID",
        time: "Time",
        malfunctionOrDiagnostic: "Malfunction or data diagnostic",
        isCleared: "Cleared",
      },
      malfunctions: {
        P: "Power compliance malfunction",
        E: "Engine synchronization compliance malfunction",
        T: "Timing compliance malfunction",
        L: "Positioning compliance malfunction",
        R: "Data recording compliance malfunction",
        S: "Data transfer compliance malfunction",
        O: "Other ELD detected malfunction",
        1: "Power data diagnostic event",
        2: "Engine synchronization data diagnostic event",
        3: "Missing required data elements data diagnostic event",
        4: "Data transfer data diagnostic event",
        5: "Unidentified driving records data diagnostic event",
        6: "Other ELD unidentified diagnostic event",
      },
      malfunctionDescriptions: {
        P:
          "ELD was not powered for an aggregated in-motion driving time of 30 minutes or more over a 24-hour period",
        E:
          "ECM connectivity to any of the required data sources (engine power status, vehicle motion status, miles driven, and engine hours) was lost for more than 30 minutes during a 24-hour period",
        T:
          "ELD could no longer meet the underlying compliance requirement to record Coordinated Universal Time (UTC), where ELD time must be synchronized with UTC, not to exceed an absolute deviation of 10 minutes at any time",
        L:
          "ELD failed to acquire a valid position measurement within 5 miles of the commercial motor vehicle moving and 60 minutes had passed",
        R:
          "ELD could no longer record or retain required events or retrieve recorded logs that are not kept remotely by the motor carrier",
        S:
          "ELD stayed in the unconfirmed data transfer mode following the next three consecutive monitoring checks",
        O: "",
        1: "ELD was not powered and fully functional within one minute of the vehicle's engine receiving power and did not remain powered for as long as the vehicle's engine stayed powered",
        2: "ELD lost ECM connectivity to any of the required data sources (engine power status, vehicle motion status, miles driven, and engine hours) and could no longer acquire updated values for the required ELD parameters within five seconds of the need.",
        3: "Some of the required data fields were missing at the time of their recording",
        4: "Data transfer data diagnostic event",
        5: "The operation of the data transfer mechanism(s) was not confirmed",
        6: "",
      },
    },
    "fmcsa-data-transfer": {
      name: "FMCSA inspections",
      title: "FMCSA data transfer",
      notifications: {
        createSuccess: "Data queued for send to FMCSA successfully",
        markedUnofficial:
          "FMCSA inspection has been marked unofficlal successfully",
      },
      labels: {
        driver: "Driver",
        vehicle: "Vehicle (leave empty for current vehicle)",
        dateFrom: "Date from",
        dateTo: "Date to",
        comment: "Comment",
        submitToFmcsa: "Submit to FMCSA",
        generateEldFile: "Generate ELD file",
      },
      messages: {
        unspecifiedError:
          "An error has occurred. Check the console for more info.",
        fileGenerated:
          "File generated successfully. Check your downloads folder.",
        fileSubmitted: "File submitted successfully",
      },
    },
    "locations-current": {
      name: "Vehicle locations",
      labels: {
        odometerCopied: "Odometer copied to clipboard",
        plugged: "Plugged",
        unplugged: "Unplugged",
        disconnected: "Disconnected",
        unidentifiedDriving: "Unidentified driving",
        connected: "Connected",
        vehicleId: "Vehicle ID",
        driverName: "Driver name",
        dutyStatus: "Duty status",
        drivingSpeed: "Driving speed",
        eldConnection: "ELD connection",
        goToLiveTracking: "Go to live tracking",
        backToMap: "Back to map",
        shareLinks: "Share links",
        noShareLinks: "No share links to display",
        copyLink: "Copy link",
        linkCopiedSuccessfully: "Link copied successfully",
        validFrom: "Valid from",
        validTo: "Valid to",
        generate: "Generate",
        generateNew: "Generate new",
        unknownDriver: "Unknown driver",
        none: "None",
        geolocation: "Geolocation",
        trailers: "Trailer(s)",
        mustBeInFuture: "The time must be in the future",
        motionStatus: "Motion status",
        motionStatusDuration: "Status duration",
        odometer: "Odometer",
        trailerName: "Name",
      },
    },
    "locations-trailers": {
      name: "Trailer locations",
    },
    "locations-livetrack": {
      name: "Live tracking",
      labels: {
        followVehicle: "Follow vehicle",
        liveUpdatesUnavailable:
          "Live updates unavailable. Showing the last known location.",
        liveUpdatesPaused:
          "Live updates paused. Showing the last known location.",
        waitingForLiveUpdates:
          "Waiting for live updates. Showing last known location.",
      },
    },
    "locations-history": {
      name: "Locations history",
      labels: {
        pleaseSelectVehicle: "Please select a vehicle",
        noDataForSelectedFilter: "No data for the selected filter",
        vehicle: "Vehicle",
      },
    },
    eldDisconnections: {
      name: "ELD Disconnections",
    },
    vehicles: {
      name: "Vehicles",
    },
    trailers: {
      name: "Trailers",
    },
    "assets-dvir-history": {
      name: "DVIR history",
      labels: {
        dateFrom: "DVIRs from",
        dateTo: "DVIRs to",
        vehicleName: "Vehicle ID",
        vehicleMake: "Vehicle make",
        hasDefects: "Defects",
        hasUnresolvedDefects: "Defects resolved",
      },
    },
    "assets-dvir-vehicle-history": {
      name: "Vehicle DVIR history",
      types: {
        PreTrip: "Pre-trip inspection",
        PostTrip: "Post-trip inspection",
      },
      labels: {
        date: "Date",
        driverFullName: "Driver",
        type: "Type",
        trailerName: "Trailer",
        minorDefectsCount: "Minor defects",
        majorDefectsCount: "Major defects",
        vehicleName: "Vehicle",
        odometer: "Odometer",
        companyName: "Company",
        location: "Location",
        dateTime: "Date & time",
        area: "DVIR area",
        point: "DVIR point",
        defectDescription: "Defect",
        severity: "Severity",
        photos: "Photos",
        isResolved: "Resolved",
        defects: "Defects list",
        comment: "Comment",
        reopen: "Reopen",
        resolve: "Resolve",
        resolveDefect: "Resolve defect",
        defectCorrected: "Defect corrected",
        defectNoNeedToBeCorrected: "Defect doesn't need to be corrected",
        confirmReopenTitle: "Confirm reopen",
        confirmReopenMessage:
          "Are you sure you want to reopen this resolved defect?",
      },
    },
    "assets-maintenance": {
      name: "Maintenance",
      labels: {
        vehicleName: "Vehicle ID",
        vehicleMake: "Make",
        vehicleYear: "Manufactured",
        vehicleOdometer: "Odometer",
        tiresStatus: "Tires status",
        lastMaintenanceDate: "Last maintenance date",
        noProblemReported: "No problem reported",
        axles: "Axles",
        axle: "Axle",
        tires: "Tires",
        tire: "Tire",
        psi: "PSI",
        temperature: "Temp (°C)",
        airLeakageRate: "Air leakage",
      },
    },
    "assets-vehicle-maintenance": {
      name: "Vehicle oil change",
      title: {
        edit: "Edit vehicle oil change",
        create: "Create a vehicle oil change",
      },
      labels: {
        date: "Date",
        isRegularMaintenance: "Regular oil change",
        nextMaintenanceDate: "Next oil change due date",
        nextMaintenanceOdometer: "Next oil change due mileage",
        notes: "Notes",
        nextMaintenanceNotes: "Notes for the next oil change",
        previousOilChangeId: "Previous oil change",
      },
      notifications: {
        createSuccess: "Vehicle oil change data created successfully",
        editSuccess: "Vehicle oil change data edited successfully",
      },
    },
    "assets-malfunctions-diag": {
      name: "Diagnostics",
    },
    malfunctionsDiag: {
      name: "Diagnostics",
      labels: {
        time: "Time",
        vehicleName: "Vehicle ID",
        vehicleMake: "Make",
        vehicleModel: "Model",
        driverFullName: "Driver",
        dtcSource: "Source",
        spn: "Suspect",
        fmi: "Failure",
        acknowledged: "Acknowledged",
        notAcknowledged: "Not acknowledged",
        both: "Both",
        acknowledge: "Acknowledge",
        dateFrom: "Display data from",
        dateTo: "Display data to",
        acknowledge_title: "Acknowledge diagnostic record",
        acknowledge_content:
          "Are you sure you want to acknowledge this diagnostic record? If it's not cleared from the vehicle, it will appear again.",
        type: "Type",
      },
    },
    reports: {
      labels: {
        reportRequested:
          "Report is being generated, you will be notified when it is ready",
        success: "Report created successfully, check your downloads",
        error:
          "Failed to create the report, check the console for more information",
        period: "Period",
        thisWeek: "This Week",
        lastWeek: "Last Week",
        thisMonth: "This Month",
        lastMonth: "Last Month",
      },
      iftaMileage: {
        name: "IFTA mileage report detail",
        description:
          "IFTA mileage summary report with vehicles daily mileage breakdown by state",
      },
      iftaSummary: {
        name: "IFTA summary report",
        description:
          "IFTA mileage summary report with vehicles mileage breakdown by state",
      },
      vehicleMileage: {
        name: "Vehicle mileage report",
        description:
          "Vehicle mileage summary report with vehicles daily mileage breakdown by state",
      },
      tripReportDetail: {
        name: "Trip report detail",
        description: "Trip report detail with vehicles daily mileage breakdown",
      },
      tripDetail: {
        name: "Trip detail",
        description: "Trip detail with vehicles daily mileage breakdown",
      },
      safety: {
        name: "Safety report",
        description:
          "Driver safety analysis report tracking driving behavior, alerts, and incidents",
      },
      iftaMileageBasic: {
        name: "IFTA mileage",
        description:
          "IFTA mileage summary report with vehicles mileage breakdown by state",
      },
      vehicleMileageSummary: {
        name: "Vehicle mileage summary report",
        description:
          "Vehicle mileage summary report with vehicles mileage breakdown by state",
      },
      vehicleDistance: {
        name: "Vehicle distance report",
        description:
          "Vehicle mileage summary report with daily mileage breakdown",
      },
      vehicleInactivity: {
        name: "Vehicle inactivity report",
        description:
          "Vehicle inactivity summary report with daily inactive hours detailed breakdown",
      },
      locationsHistory: {
        name: "Locations history report",
        description: "Breakdown of locations history for each vehicle",
      },
      eldEvents: {
        name: "ELD events report",
        description:
          "Vehicle unidentified and identified mileage summary report with daily mileage breakdown",
      },
      dailyLogs: {
        name: "Daily logs report",
        description:
          "Daily log reports for each day of the selected time period",
      },
      dvirHistory: {
        name: "DVIR report",
        description: "DVIR inspection reports in the selected time period",
      },
      fuelEconomy: {
        name: "Fuel Economy Report",
        description:
          "Fuel economy analysis report tracking fuel consumption and efficiency metrics",
      },
      harshDriving: {
        name: "Harsh driving reports",
        description:
          "View detailed information about drivers' harsh driving behavior",
      },
      idleReports: {
        name: "Idle reports",
        description: "View detailed information about drivers' idle behavior",
      },
    },
    "admin-mobile-app-versions": {
      name: "Mobile app versions",
      title: {
        create: "Add a new mobile app version",
      },
      notifications: {
        createSuccess: "Mobile app version added successfully",
      },
    },
    "admin-companies": {
      name: "Companies",
      title: {
        edit: "Edit company",
        create: "Create a company",
      },
      notifications: {
        createSuccess: "Company data created successfully",
        editSuccess: "Company data updated successfully",
      },
      labels: {
        companyDetails: "Company details",
        name: "Company name",
        street: "Street",
        city: "City",
        postalCode: "ZIP / Postal code",
        dot: "DOT #",
        hosDefaultSettings: "HOS default settings",
        homeTerminalTimeZone: "Home terminal time zone",
        cycleRules: "Cycle rules",
        cargoType: "Cargo type",
        thirtyMinuteBreakException: "30 minute break exception",
        twentyFourHrCycleReset: "24 hour cycle reset",
        dvirWarnings: "Inspection report (DVIR) warnings",
        odometer: "Odometer type",
        otherSettings: "Other settings",
        fleetOverSpeedingThreshold: "Fleet over speeding threshold",
      },
    },
    "master-fleet-managers": {
      name: "Master users",
      title: {
        edit: "Edit master user",
        create: "Create a master user",
      },
      notifications: {
        createSuccess: "Master user data created successfully",
        editSuccess: "Master user data updated successfully",
      },
    },
    "admin-fleet-managers": {
      name: "Fleet managers",
      title: {
        edit: "Edit fleet manager",
        create: "Create a fleet manager",
      },
      notifications: {
        createSuccess: "Fleet manager data created successfully",
        editSuccess: "Fleet manager data updated successfully",
        passwordResetSuccess:
          "A password reset link has been sent successfully",
      },
      labels: {
        personalInformation: "Personal information",
        fullName: "Full name",
        name: "First name",
        surname: "Last name",
        managerSettings: "Manager settings",
        companyName: "Company name",
        role: "Role",
        canEditLogs: "User can edit driver logs",
        canResolveDefects: "User can resolve defects",
        groups: "Access to groups",
        allGroups: "All groups",
      },
    },
    "admin-drivers": {
      name: "Drivers",
      title: {
        edit: "Edit driver",
        create: "Create a driver",
      },
      notifications: {
        createSuccess: "Driver data created successfully",
        editSuccess: "Driver data updated successfully",
        passwordChanged: "Password changed successfully",
        passwordChangeFailed: "Unable to change the password",
      },
      labels: {
        personalInformation: "Personal information",
        hosEldOptions: "HoS & ELD options",
        documents: "Documents",
        driverId: "Driver ID",
        companyName: "Company name",
        breakException30mins: "30-minute break exception",
        cycleReset24hours: "24-hour cycle reset",
        dateOfBirth: "Date of birth",
        address: "Address",
        driverType: "Driver type",
        licenceNumber: "License number",
        cargoType: "Cargo type",
        exemptFromEld: "Exempt from ELD",
        allowYardMove: "Allow yard move",
        allowPersonalConveyance: "Allow personal conveyance",
        allowManualDriveTime: "Allow manual drive time",
        hideUnidentifiedEvents: "Hide unidentified events",
        name: "First name",
        surname: "Last name",
        cycleRules: "Cycle rules",
        filterByDriverStatus: "Filter by driver status",
        displayName: "Name",
        personal: "Personal",
        yardMove: "Yard move",
        mobAppType: "Mobile app",
        password: "Password",
        viewPassword: "View password",
        viewPasswordTitle: "View password for %{driver}",
        changePassword: "Change password",
        changePasswordTitle: "Change password for %{driver}",
        newPassword: "New password",
        confirmTitle: "Confirm driver deactivation",
        confirmContent:
          "Before deactivating a driver, it is important to confirm that the driver has no active sessions in the system and has successfully signed out of the application. Are you sure you want to perform this action?",
      },
    },
    "admin-vehicles": {
      name: "Vehicles",
      title: {
        edit: "Edit vehicle",
        create: "Create a vehicle",
      },
      notifications: {
        createSuccess: "Vehicle data created successfully",
        editSuccess: "Vehicle data updated successfully",
      },
      labels: {
        odometerOffset: "Odometer offset",
        generalInformation: "General information",
        vin: "VIN",
        licencePlateNumber: "License plate number",
        odometerType: "Odometer type",
        fuelType: "Fuel type",
        companyInformation: "Company information",
        vehicleId: "Vehicle ID",
        company: "Company",
        companyOwned: "Company owned",
        eldSerialNumber: "Assigned ELD S/N",
        make: "Make",
        model: "Model",
        year: "Year",
        color: "Color",
        noGroups: "No groups",
        noDocuments: "No documents",
        noEldDevice: "No ELD device",
      },
    },
    "admin-trailers": {
      name: "Trailers",
      title: {
        edit: "Edit trailer",
        create: "Create a trailer",
      },
      labels: {
        name: "ID",
        type: "Type",
        make: "Make",
        year: "Year",
        licencePlate: "License plate",
        ownership: "Ownership",
        suspension: "Suspension",
        general: "General",
        company: "Company",
        companyInfo: "Company info",
        vin: "VIN",
      },
      notifications: {
        createSuccess: "Trailer data created successfully",
        editSuccess: "Trailer data updated successfully",
      },
    },
    "admin-trailer-gps-providers": {
      name: "Trailer GPS providers",
      title: {
        edit: "Edit trailer GPS provider",
        create: "Create a trailer GPS provider",
      },
      labels: {
        provider: "Provider",
        status: "Status",
        appToken: "App token",
        username: "User name",
        password: "Password",
        authToken: "Authentication token",
        companyId: "Company ID",
      },
      notifications: {
        createSuccess: "Trailer GPS provider created successfully",
        editSuccess: "Trailer GPS provider updated successfully",
      },
    },
    "admin-fuel-providers": {
      name: "Fuel providers",
      title: {
        edit: "Edit fuel provider",
        create: "Create a fuel provider",
      },
      labels: {
        provider: "Provider",
        status: "Status",
        authToken: "Authentication token",
        userName: "User name",
        password: "Password",
        name: "Name",
        lastSuccessfulSync: "Last successful sync",
        inactive: "Inactive",
        degraded: "Degraded",
        autoDisabledBySystem: "Auto-disabled by System",
        nextAttempt: "Next attempt",
        disabled: "Disabled",
        never: "Never",
      },
      notifications: {
        createSuccess: "Fuel provider created successfully",
        editSuccess: "Fuel provider updated successfully",
      },
    },
    "admin-groups": {
      name: "Groups",
      title: {
        edit: "Edit group",
        create: "Create a group",
      },
      labels: {
        name: "Group name",
        creatorUserDisplayName: "Creator",
        general: "General",
        company: "Company",
        groupMembers: "Group members",
        drivers: "Drivers",
        vehicles: "Vehicles",
        noVehicles: "No vehicles",
        noDrivers: "No drivers",
        selectedVehicles: "Selected vehicles",
        selectedDrivers: "Selected drivers",
      },
      notifications: {
        createSuccess: "Group data created successfully",
        editSuccess: "Group data updated successfully",
      },
    },
    "admin-roles": {
      name: "Roles & Permissions",
      title: {
        create: "Create a role",
        edit: "Edit role",
      },
      labels: {
        name: "Name",
        isDefault: "Default",
        isStatic: "Static",
        generalInformation: "General information",
        actionPermissions: "Action permissions",
        fieldPermissions: "Field permissions",
        anotherRole: "another role",
        copyActionPermissions: "Copy action permissions from",
        copyFieldPermissions: "Copy field permissions from",
        deleteRole: "Delete role",
      },
      notifications: {
        createSuccess: "Role data created successfully",
        editSuccess: "Role data updated successfully",
      },
    },
    "admin-elds": {
      name: "ELDs",
      title: {
        create: "Register a new ELD",
        edit: "Edit ELD",
      },
      labels: {
        vendor: "Vendor",
        serialNumber: "S/N",
        vehicleName: "Assigned to vehicle",
        deviceType: "Device type",
        macAddress: "MAC address",
        deviceName: "Device name",
      },
      notifications: {
        createSuccess: "New ELD registered successfully",
        editSuccess: "ELD data updated successfully",
      },
    },
    "admin-eld-messages": {
      name: "ELD messages",
    },
    "admin-notifications": {
      name: "Notifications",
      title: {
        create: "Create a notification subscription",
        edit: "Edit notification subscription",
      },
      labels: {
        name: "Notification subscription name",
        emailRecipients: "E-mail recipients",
        noEmailRecipients: "No e-mail recipients",
        smsRecipients: "SMS message recipients",
        noSmsRecipients: "No SMS message recipients",
        notificationSettings: "Settings",
        sendWindowStartTime: "Do not send notifications before",
        sendWindowEndTime: "Do not send notifications after",
        youMustSpecifyBothTimes:
          "Either specify both start and end time, or specify none",
        notificationRecipients: "Recipients",
        email: "E-mail address",
        smsNumber: "SMS number",
        events: "Events",
        drivers: "Drivers",
        vehicles: "Vehicles",
        urgent: "Notify immediately",
        eventType: "Notification event type",
      },
      notifications: {
        createSuccess: "Notification subscription created successfully",
        editSuccess: "Notification subscription updated successfully",
      },
      notificationTypes: {
        Driver: {
          name: "Drivers",
          DOCUMENTS: "Document is about to expire",
          PENDING_EDIT_REQUEST_REJECTED: "Pending edit request rejected",
          PRE_TRIP_DVIR: "Pre-trip DVIR not performed",
          VIOLATION_WARNINGS: "HoS violation",
          FMCSA_INSPECTION: "FMCSA inspection",
          SHIFT_MUST_END_SOON: "Upcoming 14-hour violation",
          CYCLE_MUST_END_SOON: "Upcoming 34-hour violation",
          SHIFT_DRIVING_VIOLATION_SOON: "Upcoming 11-hour violation",
          DRIVING_RUN_VIOLATION_SOON: "Upcoming 30 minute break violation",
        },
        Vehicle: {
          name: "Vehicles",
          ALARM_END: "Geofence alarm expired",
          BEGIN_STOP: "Vehicle is about to stop",
          CLUSTER: "Cluster data received",
          DMCHANGE: "J1939 diagnostic message read",
          DOCUMENTS: "Document is about to expire",
          DVIR_DEFECTS: "DVIR defects found",
          HEARTBEAT: "Heartbeat",
          HEADING: "Change in direction",
          ROLLOVER: "Rolled over due to an accident",
          EMERGENCY_END: "Panic mode emergency timer expired",
          IDLING_END: "No longer idling",
          FENCEENTRY: "Moved back in the geofence radius",
          HARDSTOP: "Possible crash or impact",
          HARDBRAKE: "Harsh braking detected",
          HARDTURN: "Harsh turn detected",
          HARDACCEL: "Harsh acceleration detected",
          AI_TAILGATE: "Tailgating detected",
          AI_OBSVIEW: "Camera view obstructed",
          AI_DISTRACT: "Driver distraction detected",
          MILON: "Malfunction Indicator Lamp turned on",
          MILOFF: "Malfunction Indicator Lamp turned off",
          VIN: "VIN is detected for the first time or changed",
          FUELLOSS: "Significant drop in fuel level detected",
          REFUEL: "Significant rise in fuel level detected",
          MOVING: "Vehicle moving",
          SPEEDING_END: "No longer speeding",
          FENCEEXIT: "Moved out of the geofence radius",
          SPEEDING: "Speed went beyond the speed limit",
          UNIDENTIFIED_DRIVING: "Unidentified driving",
          POWER_UP: "ELD powered up",
          IGNITION_OFF: "Ignition off",
          IGNITION_ON: "Ignition on",
          ALARM: "Geofence alarm tripped",
          POWER_OFF: "ELD switched off due to low battery",
          ON_PERIODIC: "Active periodic timer fired while ignition ON",
          OFF_PERIODIC: "Active periodic timer fired while ignition OFF",
          POLL: "ELD received a POLLQ command",
          EMERGENCY: "Emergency button being pressed by the driver",
          BATT_WARN: "Battery low threshold reached",
          IDLING: "Idling with the ignition on",
          POWER_CUT: "ELD physically disconnected from the vehicle",
          END_STOP: "Vehicle stopped",
          MOTOALARM:
            "Motion detected with ignition off and motion alarm enabled",
          CONN: "ELD connection established (Geometris)",
          DISCONN: "ELD disconnected (Geometris)",
          MOTALARM: "Vehicle in motion with ignition off",
          BUSMALF: "Vehicle is moving but no ECU Data is detected",
          BUSMALF_END:
            "ECU data detected after previously not able to detect while moving",
          CELL: "ELD acquired cell signal",
          VEHICLE_MAINTENANCE: "Vehicle maintenance alert",
          IGN_OFF: "Ignition off",
          IGN_ON: "Ignition on",
          NOCELL: "ELD lost cell signal",
          ELD_DISCONNECTED: "ELD disconnected",
        },
        Other: {
          name: "Other events",
          TRAILER_DOCUMENTS: "Trailer document is about to expire",
        },
      },
    },
    "admin-dvir-forms": {
      name: "DVIR Forms",
      title: {
        create: "Create a DVIR form",
        edit: "Edit DVIR form",
      },
      notifications: {
        createSuccess: "DVIR form created successfully",
        editSuccess: "DVIR form updated successfully",
      },
      labels: {
        name: "Name",
        isDefault: "Is default",
        dvirForm: "DVIR form",
        areas: "Areas",
        areaName: "Area name",
        areaPoints: "Area points",
        pointName: "Point name",
        severity: "Severity",
        duplicateName: "Duplicate name",
      },
      severities: {
        minor: "Minor",
        major: "Major",
        Minor: "Minor",
        Major: "Major",
      },
    },
    "admin-api-clients": {
      name: "API clients",
      title: {
        create: "Register an API client",
        edit: "Edit API client",
      },
      notifications: {
        createSuccess: "API client registered successfully",
        editSuccess: "API client registration updated successfully",
      },
      labels: {
        name: "Name",
        description: "Description",
        value: "Key",
        valueHelperText:
          "Copy this key before closing the form, it won't be displayed again",
        basicDetails: "Basic details",
        keys: "Keys",
        clientId: "Client ID",
        redirected: "Redirected",
        status: "Status",
        view: "View",
        company: "Company",
        createRedirectedApi: "Create redirected API",
        apiKey: "API key",
        createRedirectedApiSuccess:
          "Redirected API client created successfully",
      },
    },
    "admin-flow-api-clients": {
      name: "Flow API clients",
      title: {
        create: "Register a Flow API client",
        edit: "Edit Flow API client",
      },
      notifications: {
        createSuccess: "Flow API client registered successfully",
        editSuccess: "Flow API client registration updated successfully",
      },
      labels: {
        name: "Name",
        description: "Description",
        value: "Key",
        valueHelperText:
          "Copy this key before closing the form, it won't be displayed again",
        basicDetails: "Basic details",
        keys: "Keys",
        clientId: "Client ID",
        company: "Company",
      },
    },
    "admin-eld-export": {
      name: "ELD file Export",
      fields: {
        fileName: "File Name",
        creationTime: "Creation Time",
        creator: "Creator",
        description: "Description",
        status: "Status",
        downloadUrl: "Download",
      },
      labels: {
        filterByExportStatus: "Filter by export status",
        dateFrom: "Date from",
        dateTo: "Date to",
      },
      messages: {
        exportReady:
          "ELD files export started. You will be notified when it is ready.",
        exportFailed: "ELD files export failed.",
        exportDescription:
          "from %{dateFrom} to %{dateTo} for %{driverCount} driver(s)",
      },
    },
    "admin-eld-import": {
      title: {
        create: "Upload ELD file(s) to import",
        import: "Finish importing ELD file(s)",
      },
      name: "ELD file Import",
      fields: {
        fileName: "File Name",
        creationTime: "Creation Time",
        creator: "Creator",
        description: "Description",
        status: "Status",
        downloadUrl: "Download",
      },
      labels: {
        filterByExportStatus: "Filter by export status",
        dateFrom: "Date from",
        dateTo: "Date to",
        creationTime: "Creation time",
        creatorUserName: "Requested by",
        description: "Description",
        filesCount: "Files count",
        status: "Import status",
      },
      statuses: {
        Created: "Created",
        Parsing: "Parsing...",
        ParsingFailed: "Parsing failed",
        ReadyToImport: "Ready to import",
        Importing: "Importing...",
        ImportFailed: "Import failed",
        ImportSuccess: "Import success",
      },
      notifications: {
        createSuccess:
          "Successfully uploaded ELD file(s), please wait for them to be parsed",
        importSuccess: "Successfully initiated the import of ELD file(s)",
      },
      messages: {
        exportReady:
          "ELD files export started. You will be notified when it is ready.",
        exportFailed: "ELD files export failed.",
        exportDescription:
          "from %{dateFrom} to %{dateTo} for %{driverCount} driver(s)",
      },
    },
    "master-tenants": {
      name: "Customers",
      title: {
        create: "Create a customer",
        edit: "Edit customer",
      },
      labels: {
        name: "Name",
        customerContactEmail: "Contact e-mail",
        customerContactName: "Contact person",
        customerContactPhone: "Contact phone",
        appUrl: "Application web address",
        webApiUrl: "API web address",
        adminName: "Administrator name",
        adminEmailAddress: "Administrator e-mail for login",
        generalInformation: "General information",
        hostingInformation: "Hosting information",
        dbServer: "Database server address",
        dbName: "Database name",
        dbUsername: "Database username",
        dbPassword: "Database password",
        hasOwnElds: "Has own ELDs",
        domainName: "Customer domain name",
        mustBeValidDomainName: "Must be a valid domain name",
        tenantRegistrationType: "Customer type",
        noCertifyIfTrailerOrBolMissing:
          "Don't certify if trailer or BOL missing",
        noNewShiftIfNoCertify: "Don't allow new shift without certification",
      },
      notifications: {
        createSuccess: "Tenant data created successfully",
        editSuccess: "Tenant data updated successfully",
      },
    },
    "master-tenant-registrations": {
      name: "Customer registrations",
      title: {
        create: "Register a new customer",
        edit: "Edit customer",
        approve: "Approve the customer registration",
      },
      labels: {
        status: "Registration status",
        statusTimestamp: "Last status change",
        parentTenantId: "Belongs to customer",
        hasMasterAccount: "Grant master account",
        approve: "Approve",
        reject: "Reject",
        reject_title: "Reject the requested status",
        reject_content: "Are you sure you want to reject the requested status?",
        approve_title: "Approve the requested status",
        approveReactivation_title: "Reactivate the customer",
        approve_content:
          "WARNING: This action will permanently delete the customer database and cannot be undone. Are you sure you want to approve the requested customer deletion?",
        aproveDeactivation_content:
          "WARNING: This action will cause the customer to not be able to use ProLogs anymore. Are you sure you want to approve the requested customer deactivation?",
        aproveReactivation_content:
          "WARNING: This action will cause the customer to be able to use ProLogs again with all of the driver and fleet manager accounts being reactivated. Are you sure you want to reactivate this customer?",
        delete_title: "Delete the selected tenant",
        delete_content:
          "WARNING: This action will permanently delete the customer and cannot be undone. Are you sure you want to approve the requested customer deletion?",
      },
      statuses: {
        [TenantRegistrationStatus.Active]: "Active",
        [TenantRegistrationStatus.DeletePending]: "Delete pending",
        [TenantRegistrationStatus.DeleteRejected]: "Delete rejected",
        [TenantRegistrationStatus.Deleted]: "Deleted",
        [TenantRegistrationStatus.RegistrationPending]: "Registration pending",
        [TenantRegistrationStatus.RegistrationRejected]:
          "Registration rejected",
        [TenantRegistrationStatus.Deleting]: "Deleting",
        [TenantRegistrationStatus.DeletionFailed]: "Delete failed",
        [TenantRegistrationStatus.Registering]: "Registering",
        [TenantRegistrationStatus.RegistrationFailed]: "Registration failed",
        [TenantRegistrationStatus.DeactivationPending]: "Deactivation pending",
        [TenantRegistrationStatus.Deactivated]: "Deactivated",
        [TenantRegistrationStatus.Deactivating]: "Deactivating",
        [TenantRegistrationStatus.DeactivationFailed]: "Deactivation failed",
        [TenantRegistrationStatus.DeactivationRejected]:
          "Deactivation rejected",
        [TenantRegistrationStatus.Reactivating]: "Reactivating",
        [TenantRegistrationStatus.ReactivationFailed]: "Reactivation failed",
      },
      notifications: {
        createSuccess: "Customer registration data created succesfully",
        editSuccess: "Customer registration data updated succesfully",
        revokeMasterSuccess: "Master account revoked succesfully",
        grantMasterSuccess: "Master account granted succesfully",
        reassignSuccess: "Customer registration reassigned succesfully",
        deactivateSuccess:
          "Customer registration deactivation started succesfully",
        reactivateSuccess:
          "Customer registration reactivation started succesfully",
        masterSuspendSuccess: "Master access suspended succesfully",
        masterReactivateSuccess: "Master access reactivated succesfully",
      },
    },
    "master-elds": {
      name: "ELDs",
      title: {
        create: "Register a new ELD",
        edit: "Edit ELD",
      },
      labels: {
        vendor: "Vendor",
        deviceType: "Device type",
        serialNumber: "ELD S/N",
        assignedToTenant: "Assigned to tenant",
        generalInformation: "General information",
        assignedToTenantId: "Assigned to tenant",
        noTenant: "(No assigned tenant)",
        assign: "Assign to tenant",
        unassign: "Unassign from tenant",
        confirmUnassignTitle: "Confirm unassign",
        confirmUnassignMessage:
          "Are you sure you want to unassign the selected ELDs?",
        confirmAssignTitle: "Select a tenant to assign the selected ELDs to:",
        unassignFailed: "Failed to unassign ELDs",
        assignFailed: "Failed to assign ELDs",
        assignSuccessful: "Successfully assigned the selected ELDs",
        unassignSuccessful: "Successfully unassigned the selected ELDs",
        macAddress: "MAC address",
        deviceName: "Device name",
      },
      notifications: {
        createSuccess: "ELD data created successfully",
        editSuccess: "ELD data updated successfully",
      },
    },
    "admin-tenant-own-elds": {
      name: "Customer-owned ELDs",
    },
    "admin-virtual-tenants": {
      name: "Virtual customers",
      title: {
        create: "Add a new virtual customer",
        edit: "Edit a virtual customer",
      },
      notifications: {
        createSuccess: "Virtual customer added successfully",
        editSuccess: "Virtual customer updated successfully",
      },
      labels: {
        useDefaultValues: "Use Default values",
        customerName: "Customer Name",
        dot: "DOT number",
      },
      fields: {
        email: "E-mail",
        password: "Password",
        lastName: "Last name",
        firstName: "First name",
        role: "Role",
      },
    },
    "public-api-redirection": {
      name: "Redirected API clients",
      labels: {
        filterByStatus: "Filter by status",
        active: "Active",
        pending: "Pending",
        provider: "Provider",
        tenant: "Tenant",
        status: "Status",
        approve: "Approve",
        approve_title: "Approve the redirected API client",
        approve_content:
          "Are you sure you want to approve the redirected API client?",
        reject: "Reject",
        reject_title: "Reject the redirected API client",
        reject_content:
          "Are you sure you want to reject the redirected API client?",
      },
    },
    "master-subscriptions": {
      name: "Subscriptions",
      title: {
        create: "Create a subscription",
        edit: "Edit subscription",
      },
      labels: {
        type: "Type",
        tenantName: "Customer",
        expirationDate: "Expiration date",
        isAutoRenew: "Auto renew",
        isTrial: "Trial",
        featureNames: "Features",
        rentedEldsCount: "Number of ELDs",
        generalInformation: "General information",
        startDate: "Start date",
        minDurationMonths: "Minimum duration in months",
        durationMonths: "Duration in months",
        features: "Features",
        noFeatures: "No selected features",
        selectFeatures: "Select features to enable for this subscription",
        elds: "ELDs",
        selectElds:
          "Select ELDs to assign to the customer using this subscription",
      },
      notifications: {
        createSuccess: "Subscription created successfully",
        editSuccess: "Subscription updated successfully",
      },
    },
    "admin-demo-accounts": {
      name: "Demo accounts",
      notifications: {
        createSuccess: "Demo account successfully",
        editSuccess: "Demo account updated successfully",
      },
    },
    "maintenance-dashboard": {
      name: "Dashboard",
      labels: {
        unresolvedDvirDefects: "Unresolved DVIR defects",
        majorDefects: "Major",
        minorDefects: "Minor",
        serviceReminders: "Oil change due",
        overdue: "Overdue",
        soon: "Soon",
        totalActiveVehicles: "Active vehicles",
        vehicles: "Vehicles",
        activeDiagnosticErrors: "Active diagnostic errors",
        diagnosticErrors: "Diagnostic codes",
        vehicleAlerts: "Latest vehicle alerts",
        withDiagnosticErrors: "With diagnostic errors",
        withUnresolvedDvirDefects: "With unresolved DVIR defects",
        withEldAlerts: "With alerts from ELD",
        withTpmsAlerts: "With tire pressure alerts",
        vehicleInfo: "Vehicle info",
        name: "Vehicle ID",
        make: "Make",
        model: "Model",
        year: "Year",
        vin: "VIN",
        eldDeviceSerialNumber: "ELD S/N",
        driver: "Driver ID",
        driverName: "Driver name",
        mileage: "Mileage",
      },
    },
    "admin-geometris-commands": {
      name: "Geometris API",
      labels: {
        commandSentSuccessfully: "Command sent successfully",
        commandFailed: "Command failed",
      },
    },
  },
  customPages: {
    dataImportExport: {
      name: "Data Import / Export",
    },
    fmcsaDataTransfer: {
      name: "FMCSA Data Transfer",
    },
  },
  appData: {
    eldUsageTypes: {
      [EldDeviceUsageType.MasterAssignedToTenant]:
        "Assigned to customer by master",
      [EldDeviceUsageType.MasterUnassignedFromTenant]:
        "Unassigned from customer by master",
      [EldDeviceUsageType.TenantAssignedToVehicle]:
        "Customer assigned to vehicle",
      [EldDeviceUsageType.TenantUnassignedFromVehicle]:
        "Customer unassigned from vehicle (unused)",
    },
    cargoTypes: {
      [CargoType.Passenger]: "Passengers",
      [CargoType.Property]: "Properties",
    },
    odometerTypes: {
      [Odometer.Kilometers]: "Kilometers (km)",
      [Odometer.Miles]: "Miles (mi)",
    },
    driverTypes: {
      [DriverType.CompanyDriver]: "Company driver",
      [DriverType.DrivingForOwner]: "Driving for owner",
      [DriverType.OwnerOperator]: "Owner operator",
    },
    fuelTypes: {
      [FuelType.A55]: "A55",
      [FuelType.Biodiesel]: "Bio Diesel",
      [FuelType.CompressedNaturalGas]: "CNG",
      [FuelType.Diesel]: "Diesel",
      [FuelType.E85]: "E85",
      [FuelType.Ethanol]: "Ethanol",
      [FuelType.Gasoline]: "Gasoline",
      [FuelType.LiquidNaturalGas]: "LNG",
      [FuelType.M85]: "M85",
      [FuelType.Methanol]: "Methanol",
      [FuelType.Other]: "Other",
      [FuelType.Propane]: "Propane",
    },
    trailerTypes: {
      [TrailerType.DryVan]: "Dry van",
      [TrailerType.Flatbed]: "Flatbed",
      [TrailerType.Po]: "Power only",
      [TrailerType.Reefer]: "Reefer",
    },
    trailerSuspensionTypes: {
      [Suspension.Air]: "Air ride",
      [Suspension.Spring]: "Spring ride",
    },
    ownershipTypes: {
      [Ownership.Company]: "Company",
      [Ownership.Contractor]: "Contractor's",
      [Ownership.Leased]: "Leased",
    },
    statuses: {
      [RecordStatus.Active]: "Active",
      [RecordStatus.Inactive]: "Inactive",
    },
    dashboardMapSummary: {
      engineOff: "Engine Off",
      idle: "Idle",
      inMotion: "In Motion",
    },
    dutyCycles: {
      ChangeToDrivingStatus: "Driving",
      "ChangeToDrivingStatus-E": "Driving (E)",
      "ChangeToDrivingStatus-CR": "Driving (CR)",
      D: "Driving",
      ChangeToOffDutyStatus: "Off Duty",
      "ChangeToOffDutyStatus-E": "Off Duty (E)",
      "ChangeToOffDutyStatus-CR": "Off Duty (CR)",
      OFF: "Off Duty",
      ChangeToOnDutyNotDrivingStatus: "On Duty",
      "ChangeToOnDutyNotDrivingStatus-E": "On Duty (E)",
      "ChangeToOnDutyNotDrivingStatus-CR": "On Duty (CR)",
      ON: "On Duty",
      Dvir: "DVIR",
      "Dvir-E": "DVIR (E)",
      "Dvir-CR": "DVIR (CR)",
      ChangeToSleeperBerthStatus: "Sleeper Berth",
      "ChangeToSleeperBerthStatus-E": "Sleeper B. (E)",
      "ChangeToSleeperBerthStatus-CR": "Sleeper B. (CR)",
      SB: "Sleeper Berth",
      DriverIndicationAuthorizedPersonalUseCmv: "Pers. Conv.",
      "DriverIndicationAuthorizedPersonalUseCmv-E": "Pers. Conv. (E)",
      "DriverIndicationAuthorizedPersonalUseCmv-CR": "Pers. Conv. (CR)",
      PC: "Pers. Conv.",
      DriverIndicationYardMoves: "Yard Move",
      "DriverIndicationYardMoves-E": "Yard Move (E)",
      "DriverIndicationYardMoves-CR": "Yard Move (CR)",
      YM: "Yard Move",
      IntermediateLogConventionalLocationPrecision: "Intermediate",
      IntermediateLogReducedLocationPrecision: "Intermediate",
      AuthenticatedDriverLogin: "Login",
      AuthenticatedDriverLogout: "Logout",
      EnginePowerUpConventionalLocationPrecision: "Engine On",
      EnginePowerUpReducedLocationPrecision: "Engine On",
      EngineShutDownConventionalLocationPrecision: "Engine Off",
      EngineShutDownReducedLocationPrecision: "Engine Off",
      EldMalfunction: "Malfunction",
      EldMalfunctionClear: "Malf. CLR",
      DataDiagnostic: "Diagnostic",
      DataDiagnosticClear: "Diag. CLR",
      DriverIndicationClear: "PC/YM CLR",
      DriversFirstCertification: "Cert. #1",
      DriversSecondCertification: "Cert. #2",
      DriversThirdCertification: "Cert. #3",
      DriversFourthCertification: "Cert. #4",
      DriversFifthCertification: "Cert. #5",
      DriversSixthCertification: "Cert. #6",
      DriversSeventhCertification: "Cert. #7",
      DriversEighthCertification: "Cert. #8",
      DriversNinthCertification: "Cert. #9",
      Unknown: "Unknown",
      EldDisconnected: "ELD Disc.",
      EldConnected: "ELD Conn.",
      "Remark-CR": "Remark (CR)",
      "Remark-E": "Remark (E)",
      Remark: "Remark",
      CoDriverAdd: "Co-drv. Add",
      CoDriverRemove: "Co-drv. Rem.",
      TrailerAttach: "Trlr. Attach",
      TrailerDetach: "Trlr. Detach",
      VehicleSelect: "CMV Select",
      DriversDeCertificationOfRecords: "De-Cert.",
      VehicleStartOfDay: "Start Day",
    },
    violationTypes: {
      EightHoursViolation: "30 minutes break violation",
      ElevenHoursViolation: "11 hours violation",
      FourteenHoursViolation: "14 hours violation",
      ThirtyFourHoursResetViolation: "34 hours reset violation",
      DutyCycleViolation: "Duty cycle violation",
      ThirtyMinutesBreakViolation: "30 minutes break violation",
      TenHoursBreakRequiredViolation: "10 hours break required violation",
      UncertifiedLogsViolation: "Uncertified logs violation",
    },
    eventTypeCodes: {
      ChangeToOffDutyStatus: "Off Duty",
      ChangeToSleeperBerthStatus: "Sleeper Berth",
      ChangeToDrivingStatus: "Driving",
      ChangeToOnDutyNotDrivingStatus: "On Duty Not Driving",
      IntermediateLogConventionalLocationPrecision:
        "Intermediate (conventional loc. precision)",
      IntermediateLogReducedLocationPrecision:
        "Intermediate (reduced loc. precision)",

      DriverIndicationAuthorizedPersonalUseCmv: "Personal Conveyance",
      DriverIndicationYardMoves: "Yard Moves",
      DriverIndicationClear: "Clear PC/YM",

      DriversFirstCertification:
        "Certification or re-certification of daily log",
      DriversSecondCertification:
        "Certification or re-certification of daily log",
      DriversThirdCertification:
        "Certification or re-certification of daily log",
      DriversFourthCertification:
        "Certification or re-certification of daily log",
      DriversFifthCertification:
        "Certification or re-certification of daily log",
      DriversSixthCertification:
        "Certification or re-certification of daily log",
      DriversSeventhCertification:
        "Certification or re-certification of daily log",
      DriversEighthCertification:
        "Certification or re-certification of daily log",
      DriversNinthCertification:
        "Certification or re-certification of daily log",

      AuthenticatedDriverLogin: "Login",
      AuthenticatedDriverLogout: "Logout",

      EnginePowerUpConventionalLocationPrecision:
        "Engine power-up (conventional loc. precision)",
      EnginePowerUpReducedLocationPrecision:
        "Engine power-up (reduced loc. precision)",
      EngineShutDownConventionalLocationPrecision:
        "Engine shut-down (conventional loc. precision)",
      EngineShutDownReducedLocationPrecision:
        "Engine shut-down (reduced loc. precision)",

      EldMalfunction: "ELD malfunction",
      EldMalfunctionClear: "ELD malfunction cleared",
      DataDiagnostic: "Data diagnostic",
      DataDiagnosticClear: "Data diagnostic cleared",

      Remark: "Remark",
      EldConnected: "ELD connect",
      EldDisconnected: "ELD disconnect",
      CoDriverAdd: "Co-driver add",
      CoDriverRemove: "Co-driver remove",
      TrailerAttach: "Trailer attach",
      TrailerDetach: "Trailer detach",
      VehicleSelect: "Vehicle select",
      DriversDeCertificationOfRecords: "De-certification of daily log",
      VehicleStartOfDay: "Vehicle start of day",
    },
  },
};

export default customEnglishMessages;
